/**
 * features/expenses/ExpensesManager.tsx
 * Phase 5 — Expenses ledger. Category filter, search, running total
 * of the filtered view, and add/edit via the editor.
 */
import { useMemo, useState } from 'react';
import { Wallet, Plus, Search, Paperclip } from 'lucide-react';
import { useExpenses } from './useExpenses';
import { ExpenseEditor } from './ExpenseEditor';
import { CATEGORY_LABEL, CATEGORY_LIST } from './expenseMeta';
import { formatPula } from '@/lib/money';
import type { Expense, ExpenseCategory } from '@/types';
import { Button, Card, Input, Badge, Spinner } from '@/components/ui/primitives';

function fmtDate(iso: string) {
  return iso ? new Date(iso).toLocaleDateString('en-BW', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
}

export function ExpensesManager() {
  const { expenses, loading } = useExpenses();
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState<'all' | ExpenseCategory>('all');
  const [editor, setEditor] = useState<Expense | null | 'new'>(null);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return expenses
      .filter((e) => (cat === 'all' ? true : e.category === cat))
      .filter((e) => !q || e.payee.toLowerCase().includes(q) || e.description.toLowerCase().includes(q));
  }, [expenses, cat, search]);

  const total = useMemo(() => visible.reduce((s, e) => s + e.total, 0), [visible]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search payee or description…" className="pl-9" />
        </div>
        <Button icon={<Plus size={16} />} onClick={() => setEditor('new')}>
          New expense
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setCat('all')}
          className={`rounded-full px-3 py-1 text-sm font-semibold transition ${cat === 'all' ? 'bg-brand text-white' : 'bg-black/5 text-primaryLight hover:bg-black/10 dark:bg-white/5 dark:text-gray-300'}`}
        >
          All
        </button>
        {CATEGORY_LIST.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            className={`rounded-full px-3 py-1 text-sm font-semibold transition ${cat === c ? 'bg-brand text-white' : 'bg-black/5 text-primaryLight hover:bg-black/10 dark:bg-white/5 dark:text-gray-300'}`}
          >
            {CATEGORY_LABEL[c]}
          </button>
        ))}
      </div>

      {loading ? (
        <Spinner label="Loading expenses…" />
      ) : visible.length === 0 ? (
        <Card className="py-16 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-brandSoft text-brand">
            <Wallet size={22} />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {search || cat !== 'all' ? 'No expenses match.' : 'No expenses recorded yet.'}
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/5 text-left text-xs uppercase tracking-wide text-primaryLight dark:border-white/10 dark:text-gray-400">
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Payee</th>
                <th className="px-4 py-3 font-semibold">Description</th>
                <th className="px-4 py-3 font-semibold">Category</th>
                <th className="px-4 py-3 text-right font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((e) => (
                <tr
                  key={e.id}
                  onClick={() => setEditor(e)}
                  className="cursor-pointer border-b border-black/5 transition last:border-0 hover:bg-brandSoft/40 dark:border-white/5 dark:hover:bg-white/5"
                >
                  <td className="px-4 py-3 text-primaryLight dark:text-gray-400">{fmtDate(e.date)}</td>
                  <td className="px-4 py-3 font-medium text-primary dark:text-gray-100">{e.payee}</td>
                  <td className="px-4 py-3 text-primary dark:text-gray-200">
                    <span className="inline-flex items-center gap-1.5">
                      {e.description}
                      {e.attachmentUrl && (
                        <a
                          href={e.attachmentUrl}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(ev) => ev.stopPropagation()}
                          title="View receipt"
                          className="text-primaryLight hover:text-brand"
                        >
                          <Paperclip size={14} />
                        </a>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone="neutral">{CATEGORY_LABEL[e.category]}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-primary dark:text-gray-100">{formatPula(e.total)}</td>
                </tr>
              ))}
              <tr className="bg-brandSoft/40 dark:bg-white/5">
                <td className="px-4 py-3 font-semibold text-primary dark:text-gray-100" colSpan={4}>
                  Total ({visible.length})
                </td>
                <td className="px-4 py-3 text-right font-bold text-primary dark:text-gray-100">{formatPula(total)}</td>
              </tr>
            </tbody>
          </table>
        </Card>
      )}

      {editor !== null && <ExpenseEditor expense={editor === 'new' ? null : editor} onClose={() => setEditor(null)} />}
    </div>
  );
}
